import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

let socket = null;

const getSocketUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002/api/v1';
  // Strip trailing /api/v1 to get the base URL for socket.io
  return apiUrl.replace(/\/api\/v1\/?$/, '');
};

// Singleton socket — one connection shared across the app
export const getSocket = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  if (!socket) {
    socket = io(getSocketUrl(), {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    const isDev = process.env.NODE_ENV === 'development';
    socket.on('connect', () => {
      if (isDev) console.log('🔌 Socket connected');
    });

    socket.on('disconnect', (reason) => {
      if (isDev) console.log('🔌 Socket disconnected:', reason);
    });

    socket.on('connect_error', (err) => {
      console.warn('Socket connect error:', err.message);
    });
  }

  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

// Call this after a successful access-token refresh. The cached socket was
// opened with the OLD token — when backend rejects it, the client reconnects
// with the same stale token and loops. Swap the auth token and reconnect so
// the next handshake succeeds.
export const refreshSocketAuth = (newToken) => {
  if (socket) {
    socket.auth = { token: newToken };
    if (socket.connected) socket.disconnect();
    socket.connect();
  }
};

// Hook to subscribe to an event with automatic cleanup
export const useSocketEvent = (event, handler) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const s = getSocket();
    if (!s) return;

    const listener = (...args) => handlerRef.current(...args);
    s.on(event, listener);

    return () => {
      s.off(event, listener);
    };
  }, [event]);
};
